import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { Campo, chiave, annunciaSchedaSalvata, togliRipetizione, useCampiAutomatici } from './CampiScheda';
import BottoneRefertoDaScheda from './BottoneRefertoDaScheda';
import RiquadroGravidanze, { dopoQualeSezione } from './RiquadroGravidanze';
import RiquadroAbitudini, { SCHEDE_CON_ABITUDINI } from './RiquadroAbitudini';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/SchedeProcedura.css';
import '../../styles/SchedeProfilo.css';

/**
 * Una scheda clinica compilata nel profilo del paziente.
 *
 * QUI LA SCHEDA SI RIPETE NEL TEMPO, e cambia tutto rispetto alla
 * procedura: l'analisi di marzo e quella di settembre sono due cose
 * diverse, la seconda non sovrascrive la prima, e si confrontano. Per
 * questo la prima cosa che si vede e' l'elenco delle date: aprire
 * direttamente l'ultima nasconderebbe che le altre esistono.
 *
 * Il disegno dei campi e' lo stesso della procedura - stesso file - cosi'
 * un campo si comporta allo stesso modo ovunque lo si incontri.
 */

const dataIta = (v) => {
    if (!v) return '';
    const d = new Date(String(v).slice(0, 10));
    return isNaN(d) ? '' : d.toLocaleDateString('it-IT');
};

const SchedeProfilo = ({ pazienteId, schedaCodice, onApriAnamnesi }) => {
    const [scheda, setScheda] = useState(null);
    const [compilazioni, setCompilazioni] = useState([]);
    const [apertaId, setApertaId] = useState(null);
    const [aperta, setAperta] = useState(null);
    const [valori, setValori] = useState({});
    const [righe, setRighe] = useState({});
    /* «Modificato» = diverso dall'istantanea presa aprendo la compilazione
       e a ogni salvataggio riuscito (utils/uscitaProtetta.js). */
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ valori, righe }, versione);
    const [errore, setErrore] = useState(null);
    /* «Oggi», dal server (data italiana): una compilazione nasce oggi e la
       data non si sceglie (decisione di Alex del 23 settembre 2026, niente
       di retroattivo, come le procedure). */
    const [oggi, setOggi] = useState(null);

    /* I CAMPI SI SALVANO DA SOLI all'uscita (regola di Alex del 24/09/2026):
       niente «Salva la scheda». La compilazione aperta si legge da un
       riferimento, cosi' il salvataggio non scrive in quella di prima. */
    const apertaRef = useRef(null);
    /* I campi calcolati (migration 314) li ricalcola il database a ogni
       ingrediente salvato, e la rotta li rimanda: si mostrano quelli, senza
       ricalcolarli qui. */
    const scriviUno = useCallback(async (campoId, riga, valore) => {
        const r = await api.put(`/schede/profilo/compilazione/${apertaRef.current}`, { valori: [{ campo_id: campoId, riga, valore }] });
        const calcolati = r?.data?.data?.calcolati;
        if (calcolati && Object.keys(calcolati).length) setValori((p) => ({ ...p, ...calcolati }));
    }, []);
    const auto = useCampiAutomatici(scriviUno, useCallback(() => {
        annunciaSchedaSalvata();
        setVersione((v) => v + 1);
    }, []));
    const { fotografa } = auto;
    /* All'uscita si chiede se c'e' un campo scritto e non ancora uscito, o
       un campo rosso: il valore di un campo non salvato non va perso. */
    useUscitaProtetta(sporco || auto.nonSalvati.length > 0, auto.nonSalvati.length || null);

    const caricaElenco = useCallback(async () => {
        if (!pazienteId) return;
        try {
            const r = await api.get(`/schede/profilo/${pazienteId}`);
            const s = (r.data?.data?.schede || []).find((x) => x.codice === schedaCodice);
            setOggi(r.data?.data?.oggi || null);
            setScheda(s || null);
            setCompilazioni(s?.compilazioni || []);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere le compilazioni');
        }
    }, [pazienteId, schedaCodice]);

    useEffect(() => { caricaElenco(); }, [caricaElenco]);

    /* Cambiando scheda si chiude quella aperta: restare su una
       compilazione della scheda precedente sarebbe il modo piu' rapido di
       scrivere nel posto sbagliato. */
    useEffect(() => { setApertaId(null); setAperta(null); }, [schedaCodice]);

    const apri = useCallback(async (id) => {
        try {
            const r = await api.get(`/schede/profilo/compilazione/${id}`);
            const d = r.data?.data || {};
            setAperta(d.scheda || null);
            setValori(d.valori || {});
            apertaRef.current = id;
            fotografa(d.valori || {});
            /* Le ripetizioni si contano per SEZIONE: con un conto per
               scheda, tre farmaci facevano comparire tre interventi vuoti. */
            setRighe(d.righe_sezioni || {});
            setVersione((v) => v + 1);
            setApertaId(id);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco ad aprire la compilazione');
        }
    }, [fotografa]);

    const nuova = async () => {
        try {
            const r = await api.post(`/schede/profilo/${pazienteId}/${scheda.id}`, {});
            await caricaElenco();
            await apri(r.data.data.compilazione.id);
        } catch (e) {
            setErrore(e?.message || 'Non riesco a creare la compilazione');
        }
    };

    const quanteRighe = (sezioneId) => Math.max(1, righe[sezioneId] || 1);

    const cambia = (campoId, riga, v) => {
        setValori((p) => ({ ...p, [chiave(campoId, riga)]: v }));
    };

    /* Toglie UNA ripetizione di UNA sezione (il server tocca solo i campi
       di quella sezione e fa scalare le righe dopo). A schermo si fa la
       stessa cosa invece di ricaricare, cosi' quello che e' scritto e non
       ancora salvato altrove resta dov'e'; se non c'era niente da salvare,
       la scheda resta pulita. */
    const togliRiga = async (sez, riga) => {
        if (!window.confirm(`Togliere «${sez.ripetibile_etichetta} ${riga}»? `
                          + 'Le altre risposte della scheda restano.')) return;
        const eraPulita = !sporco;
        try {
            await api.delete(`/schede/profilo/compilazione/${apertaId}/riga/${riga}`,
                             { params: { sezione: sez.id } });
            setValori((p) => { const n = togliRipetizione(p, sez, riga); fotografa(n); return n; });
            setRighe((p) => ({ ...p, [sez.id]: Math.max(1, quanteRighe(sez.id) - 1) }));
            if (eraPulita) setVersione((v) => v + 1);
        } catch (e) {
            setErrore(e?.message || 'Non riesco a togliere la ripetizione');
        }
    };

    if (!scheda) {
        return <p className="spf-vuoto">{errore || 'Scheda non disponibile per questo paziente.'}</p>;
    }

    /* LA SCHEDA E IL SESSO (migration 235). Una scheda che vale solo per un
       sesso, su un paziente senza sesso in anagrafica, non compare: al suo
       posto la scritta, come chiesto da Alex. La regola la decide il server
       (ammessa, motivo): qui si disegna e basta. */
    if (scheda.ammessa === false && scheda.motivo === 'sesso_mancante' && compilazioni.length === 0) {
        return <p className="spf-vuoto">Sesso non indicato in anagrafica</p>;
    }

    return (
        <div className="spf">
            <div className="spf-testa">
                <div>
                    <h3>{scheda.nome}</h3>
                    {scheda.area_nome && <span>{scheda.area_nome}</span>}
                </div>
                <div className="spf-nuova">
                    {scheda.ammessa !== false && (
                    <button type="button" onClick={nuova}>
                        <Plus size={14} aria-hidden="true" /> Nuova compilazione
                        {oggi ? ` di oggi, ${oggi.split('-').reverse().join('/')}` : ''}
                    </button>
                    )}
                </div>
            </div>

            {errore && <p className="sp-errore">{errore}</p>}

            {compilazioni.length === 0 ? (
                <p className="spf-vuoto">
                    Questa scheda non è ancora stata compilata per questo paziente.
                    Premi <b>Nuova compilazione</b>: nasce con la data di oggi.
                </p>
            ) : (
                <div className="spf-date">
                    {compilazioni.map((c) => (
                        <button type="button" key={c.id}
                                className={`spf-data ${apertaId === c.id ? 'attiva' : ''}`}
                                onClick={() => apri(c.id)}>
                            {dataIta(c.data)}
                        </button>
                    ))}
                </div>
            )}

            {aperta && (
                <div className="spf-scheda">
                    {/* Fumo e alcol: il valore dell'anamnesi al giorno della
                        compilazione, in sola lettura (proposta 16). In testa,
                        perche' i campi che ne segnavano il posto sono ritirati. */}
                    {SCHEDE_CON_ABITUDINI.includes(schedaCodice) && (
                        <RiquadroAbitudini key={`ab-${apertaId}`} pazienteId={pazienteId}
                            compilazioneId={apertaId} onApriAnamnesi={onApriAnamnesi} />
                    )}
                    {aperta.sezioni.filter((z) => z.campi.length > 0).map((sez) => (
                      <React.Fragment key={sez.id}>
                        <div className="sp-sezione">
                            <div className="sp-sezione-titolo">{sez.titolo}</div>
                            {sez.ripetibile ? (
                                <>
                                    {Array.from({ length: quanteRighe(sez.id) }, (_, i) => i + 1).map((riga) => (
                                        <div className="sp-ripetizione" key={riga}>
                                            <div className="sp-ripetizione-testa">
                                                <span>{sez.ripetibile_etichetta} {riga}</span>
                                                {quanteRighe(sez.id) > 1 && (
                                                    <button type="button" onClick={() => togliRiga(sez, riga)}
                                                            title="Togli questa ripetizione">
                                                        <Trash2 size={13} />
                                                    </button>
                                                )}
                                            </div>
                                            <div className="sp-campi">
                                                {sez.campi.map((c) => (
                                                    <Campo key={c.campo_id} campo={c} riga={riga}
                                                           valore={valori[chiave(c.campo_id, riga)]}
                                                           onChange={(v) => cambia(c.campo_id, riga, v)}
                                                           onEsci={(v) => auto.esci(c.campo_id, riga, v)}
                                                           salvataggio={auto.di(c.campo_id, riga)}
                                                           onRiprova={() => auto.riprova(c.campo_id, riga, valori[chiave(c.campo_id, riga)])} />
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" className="sp-aggiungi"
                                            onClick={() => setRighe((p) => ({ ...p, [sez.id]: quanteRighe(sez.id) + 1 }))}>
                                        <Plus size={13} aria-hidden="true" /> Aggiungi {sez.ripetibile_etichetta?.toLowerCase()}
                                    </button>
                                </>
                            ) : (
                                <div className="sp-campi">
                                    {sez.campi.map((c) => (
                                        <Campo key={c.campo_id} campo={c} riga={1}
                                               valore={valori[chiave(c.campo_id, 1)]}
                                               onChange={(v) => cambia(c.campo_id, 1, v)}
                                               onEsci={(v) => auto.esci(c.campo_id, 1, v)}
                                               salvataggio={auto.di(c.campo_id, 1)}
                                               onRiprova={() => auto.riprova(c.campo_id, 1, valori[chiave(c.campo_id, 1)])} />
                                    ))}
                                </div>
                            )}
                        </div>
                        {/* Gravidanze, climaterio e menopausa: il riquadro
                            dell'anamnesi, lo stesso dato (RiquadroGravidanze). */}
                        {dopoQualeSezione(schedaCodice, aperta.sezioni) === sez.id && (
                            <RiquadroGravidanze key={apertaId} pazienteId={pazienteId} origine="scheda_ginecologica"
                                compilazioneId={apertaId} onApriAnamnesi={onApriAnamnesi} />
                        )}
                      </React.Fragment>
                    ))}
                    {dopoQualeSezione(schedaCodice, aperta.sezioni) === 'fondo' && (
                        <RiquadroGravidanze key={apertaId} pazienteId={pazienteId} origine="scheda_ginecologica"
                            compilazioneId={apertaId} onApriAnamnesi={onApriAnamnesi} />
                    )}

                    <div className="sp-azioni">
                        <BottoneRefertoDaScheda compilazioneId={apertaId} disabilitato={auto.nonSalvati.length > 0} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default SchedeProfilo;
